import type { Arhiv } from '@koda/shared';
import { useCallback, useEffect, useState, type JSX } from 'react';

import { api } from '../lib/api';
import { datum } from '../lib/format';
import { PotrdiIzbris } from '../sestavni/PotrdiIzbris';

/**
 * Arhiv (samo skrbnik).
 *
 * Arhivirano podjetje ali uporabnik izgine iz uporabe, podatki pa ostanejo:
 * iz arhiva ju je mogoče obnoviti ali dokončno izbrisati. Trajno brisanje je
 * edino dejanje, ki podatke res odnese, zato zahteva vpis naziva oziroma
 * e-naslova.
 */
export function ArhivStran(): JSX.Element {
  const [arhiv, setArhiv] = useState<Arhiv | null>(null);
  const [napaka, setNapaka] = useState<string | null>(null);

  const osvezi = useCallback((): void => {
    api
      .arhiv()
      .then(setArhiv)
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Arhiva ni bilo mogoče prebrati.');
      });
  }, []);

  useEffect(osvezi, [osvezi]);

  const ukrepaj = (obljuba: Promise<unknown>): void => {
    setNapaka(null);
    obljuba
      .then(osvezi)
      .catch((e: unknown) => {
        setNapaka(e instanceof Error ? e.message : 'Dejanje ni uspelo.');
      });
  };

  return (
    <main>
      <h1>Arhiv</h1>
      <p className="podnaslov">
        Umaknjeno iz uporabe. Podatki so ohranjeni — obnovite jih ali dokončno izbrišite.
      </p>

      {napaka !== null && <p className="napaka">{napaka}</p>}
      {arhiv === null && <p>Berem …</p>}

      {arhiv !== null && (
        <>
          <section className="kartica">
            <h2>Podjetja</h2>
            {arhiv.podjetja.length === 0 && <p>V arhivu ni podjetij.</p>}
            {arhiv.podjetja.length > 0 && (
              <ul className="seznam arhiv">
                {arhiv.podjetja.map((p) => (
                  <li key={p.id}>
                    <div>
                      <div>{p.naziv}</div>
                      <div className="drobno">
                        arhivirano {datum(p.arhivirano)} · {p.knjizb.toLocaleString('sl-SI')} knjižb
                        · vir {p.vir}
                      </div>
                    </div>
                    <div className="dnevnik-gumbi">
                      <button
                        type="button"
                        className="povezava"
                        onClick={() => {
                          ukrepaj(api.obnoviPodjetje(p.id));
                        }}
                      >
                        Obnovi
                      </button>
                      <PotrdiIzbris
                        potrditev={p.naziv}
                        navodilo={`Za trajni izbris vpišite naziv: ${p.naziv}`}
                        naIzbris={async (vpis) => {
                          await api.izbrisiPodjetje(p.id, vpis);
                          osvezi();
                        }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="kartica">
            <h2>Uporabniki</h2>
            {arhiv.uporabniki.length === 0 && <p>V arhivu ni uporabnikov.</p>}
            {arhiv.uporabniki.length > 0 && (
              <ul className="seznam arhiv">
                {arhiv.uporabniki.map((u) => (
                  <li key={u.id}>
                    <div>
                      <div>{u.ime ?? u.email}</div>
                      <div className="drobno">{u.email}</div>
                      <div className="drobno">
                        arhiviran {datum(u.arhiviran)}
                        {u.zadnjaPrijava !== null && ` · zadnja prijava ${datum(u.zadnjaPrijava)}`}
                      </div>
                    </div>
                    <div className="dnevnik-gumbi">
                      <button
                        type="button"
                        className="povezava"
                        onClick={() => {
                          ukrepaj(api.obnoviUporabnika(u.id));
                        }}
                      >
                        Obnovi
                      </button>
                      <PotrdiIzbris
                        potrditev={u.email}
                        navodilo={`Za trajni izbris vpišite e-naslov: ${u.email}`}
                        naIzbris={async (vpis) => {
                          await api.izbrisiUporabnika(u.id, vpis);
                          osvezi();
                        }}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </main>
  );
}
